<script setup>
import FormProfile from "../Components/FormProfile.vue";
import FormProgram from "../Components/FormProgram.vue";
import FormGoal from "../Components/FormGoal.vue";
import FormAnamnese from "../Components/FormAnamnese.vue";
import FormSymptoms from "../Components/FormSymptoms.vue";
import FormMedicines from "../Components/FormMedicines.vue";
import FormSave from "../Components/FormSave.vue";
import Layout from "../Layouts/Authenticated.vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { ref, unref, computed, watch, onMounted } from "vue";
import { Head, Link, useForm, usePage } from "@inertiajs/inertia-vue3";
import fields from "../utils/anamnese-fields.js";
import axios from "axios";
import { router } from '@inertiajs/vue3'
import { dataURLtoFile } from "../utils/file-utils"
import { useVuelidate } from '@vuelidate/core'
import { required, email, helpers } from '@vuelidate/validators'
import { scrollToFirstError } from "../utils/form";
import moment from 'moment'

const props = defineProps([
    "formData",
    "genderOptions",
    'max2HoursOptions',
    "rateFitnessOptions",
    'physicalOptions',
    'disciplineOptions',
    'stressLevelOptions',
    'drinkSoftdrinkOptions',
    'drinkCoffeeOptions',
    'drinkTeaOptions',
    'drinkAlcoholOptions',
    "customer",
    "errors",
    'fatPercentage',
    'visceralFatArea',
    'thyroidGlandOptions',
]);
const confirm = useConfirm();
const toast = useToast();
const active = ref("profile");
const processing = ref(false);
const errorIndex = ref([]);
const isEdit = ref(Boolean(props.customer?.id))
const isValidating = ref(false)
const isValidated = ref(isEdit.value)
const validationError = ref(null)
const isSuccess = ref(false)
const tabs = ref([
    {
        name: "Persönliche Daten",
        value: "profile",
    },
    {
        name: "Programme",
        value: "program",
    },
    {
        name: "Ziele",
        value: "goals",
    },
    {
        name: "Anamnese",
        value: "anamnese",
    },
    {
        name: "Symptome und Erkrankungen",
        value: "symptoms-and-diseases",
    },
    {
        name: "Medikamente, Injektionen & Implantate",
        value: "medicines",
    },
    {
        name: "Speichern",
        value: "save",
    },
]);

const form = useForm(Object.assign({
    accept_terms: (isEdit.value) ? true : null,
    signature_key: null,
}, props.formData));

const required$ = helpers.withMessage('Dieses Feld ist erforderlich', required)
const email$ = helpers.withMessage('Dieses Feld ist ungültig', email)
const sometimes$ = {
    $validator: function(value) {
        return true
    },
    $message: 'Dieses Feld ist erforderlich',
    $params: {
        type: 'custom'
    }
}
const true$ = {
    $validator: function(value) {
        return (value === true)
    },
    $message: 'Dieses Feld ist erforderlich',
    $params: {
        type: 'custom'
    }
}
const validDate$ = {
    $validator: function(value) {
        if (! value) {
            return true;
        }

        return moment(value, 'DD.MM.YYYY').isValid()
    },
    $message: 'Dieses Feld ist ungültig',
    $params: {
        type: 'custom'
    }
}
const validMonth$ = {
    $validator: function(value) {
        if (! value) {
            return true;
        }

        return moment(value, 'MM.YYYY').isValid()
    },
    $message: 'Dieses Feld ist ungültig',
    $params: {
        type: 'custom'
    }
}

const rules = {
    form: {
        email: { required$, email$ },
        first_name: { required$ },
        last_name: { required$ },
        street: { required$ },
        number: { required$ },
        zip_code: { required$ },
        city: { required$ },
        phone: { sometimes$ },
        mobile_phone: { required$ },
        gender: { required$ },
        birthdate: { required$, validDate$ },
        profession: { required$ },
        height: { required$ },
        weight: { required$ },

        programs: { required$ },
        max_two_hours_per_week: { required$ },

        want_to_reduce_weight: { required$ },
        reduce_weight: {
            custom: {
                $validator: function(value) {
                    if (! form.want_to_reduce_weight) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        fat_percentage: {
            custom: {
                $validator: function(value) {
                    if (! form.want_to_reduce_weight) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        visceral_fat_area: {
            sometimes$,
        },
        want_fat_deposits: { required$ },
        fat_deposit_areas: {
            custom: {
                $validator: function(value) {
                    if (! form.want_fat_deposits) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        fat_deposit_other: {
            custom: {
                $validator: function(value) {
                    return true
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },

        metabolic_function: { required$ },
        weight_loss_block: { required$ },
        satiety: { required$ },
        optimize_body_shape: { sometimes$ },
        is_reduce_cellulite: { sometimes$ },
        do_you_work: { required$ },
        do_sports: { required$ },
        what_sport: {
            custom: {
                $validator: function(value) {
                    if (! form.do_sports) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        sports_times_per_week: {
            custom: {
                $validator: function(value) {
                    if (! form.do_sports) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        long_exercise: {
            custom: {
                $validator: function(value) {
                    if (! form.do_sports) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        rate_fitness: { sometimes$ },
        physical: { required$ },
        discipline: { required$ },
        want_to_improve: { sometimes$ },
        smoker: { required$ },
        drink_water: { required$ },
        drink_softdrink: { required$ },
        drink_coffee: { required$ },
        drink_tea: { required$ },
        drink_alcohol: { required$ },

        symptoms: { sometimes$ },
        stress_level: { sometimes$ },
        allergies: {
            custom: {
                $validator: function(value) {
                    if (! required.$validator(form.symptoms)
                        || form.symptoms.indexOf('Allergien') === -1) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        heart_attack_date: {
            custom: {
                $validator: function(value) {
                    if (! required.$validator(form.symptoms)
                        || form.symptoms.indexOf('Herzinfarkt') === -1) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            },
            validMonth$
        },
        thrombosis_date: {
            custom: {
                $validator: function(value) {
                    if (! required.$validator(form.symptoms)
                        || form.symptoms.indexOf('Thrombose') === -1) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            },
            validMonth$
        },
        which_infectious_disease: {
            custom: {
                $validator: function(value) {
                    if (! required.$validator(form.symptoms)
                        || form.symptoms.indexOf('Infektionskrankheit') === -1) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        which_autoimmune_disease: {
            custom: {
                $validator: function(value) {
                    if (! required.$validator(form.symptoms)
                        || form.symptoms.indexOf('Autoimmunerkrankung') === -1) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        cancer_diagnosed_date: {
            custom: {
                $validator: function(value) {
                    if (! required.$validator(form.symptoms)
                        || form.symptoms.indexOf('Krebs') === -1) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            },
            validMonth$
        },
        cancer_diagnosed_location: {
            custom: {
                $validator: function(value) {
                    if (! required.$validator(form.symptoms)
                        || form.symptoms.indexOf('Krebs') === -1) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        operation_details: {
            custom: {
                $validator: function(value) {
                    if (! required.$validator(form.symptoms)
                        || form.symptoms.indexOf('Wurde innerhalb der letzten 2 Jahre eine Operation durchgeführt?') === -1) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        is_no_findings: {
            custom: {
                $validator: function(value) {
                    if (required.$validator(form.symptoms)) {
                        return true
                    }

                    return required.$validator(value) && value === true
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        woman_details: { sometimes$ },

        medications: { sometimes$ },
        is_no_medications: {
            custom: {
                $validator: function(value) {
                    if (required.$validator(form.medications)) {
                        return true
                    }

                    return required.$validator(value) && value === true
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        botox_last_6_months: { required$ },
        botox_details: {
            custom: {
                $validator: function(value) {
                    if (! form.botox_last_6_months) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        filler_treatment_last_6_months: { required$ },
        filler_treatment_details: {
            custom: {
                $validator: function(value) {
                    if (! form.filler_treatment_last_6_months) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        is_implants: { required$ },
        silicone: {
            custom: {
                $validator: function(value) {
                    if (! form.is_implants
                        || (form.is_implants && form.metal)) {
                        return true
                    }

                    return Boolean(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        silicone_where: {
            custom: {
                $validator: function(value) {
                    if (! form.is_implants
                        || (form.is_implants && ! form.silicone)) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        metal: {
            custom: {
                $validator: function(value) {
                    if (! form.is_implants
                        || (form.is_implants && form.silicone)) {
                        return true
                    }

                    return Boolean(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        metal_where: {
            custom: {
                $validator: function(value) {
                    if (! form.is_implants
                        || (form.is_implants && ! form.metal)) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        },
        is_active_systems: { required$ },
        implanted_active: {
            custom: {
                $validator: function(value) {
                    if (! form.is_active_systems) {
                        return true
                    }

                    return required.$validator(value)
                },
                $message: 'Dieses Feld ist erforderlich',
                $params: {
                    type: 'custom'
                }
            }
        }
    }
}

const v$ = useVuelidate(rules, {
    form,
})

const tabFields = {
    profile: fields[0],
    program: fields[1],
    goals: fields[2],
    anamnese: fields[3],
    'symptoms-and-diseases': fields[4],
    medicines: fields[5],
}

const save = async (signaturePad, isDownload) => {
    if (signaturePad) {
        const { isEmpty, data } = signaturePad.saveSignature()

        if (isEmpty) {
            props.errors.signature = ['Dieses Feld ist erforderlich']
            return
        }

        processing.value = true;

        const response = await Vapor.store(dataURLtoFile(data), {
            progress: progress => {
            }
        })

        form.signature_key = response.key
    } else {
        processing.value = true;
    }

    let postUrl = ''

    if (isEdit.value) {
        postUrl = `/customers/${props.customer.uuid}/anamnesis`

        if (isDownload) {
            postUrl = postUrl + '?download=1'
        }
    } else {
        postUrl = '/anamnesis'
    }

    form.post(
        postUrl,
        {
            preserveState: true,

            onError: (errors) => {
                setErrorIndex()

                toast.add({
                    severity: "error",
                    summary: "Error",
                    life: 5000,
                });

                if (typeof errorIndex.value[0] != 'undefined') {
                    active.value = tabs.value[errorIndex.value[0]].value;
                }
            },

            onSuccess: (page) => {
                isSuccess.value = true
                errorIndex.value = [];
                toast.add({
                    severity: "success",
                    summary: "Aktualisiert",
                    life: 5000,
                });

                if (isDownload) {
                    window.location.href = `/customers/${props.customer.uuid}/anamnesis/export`
                }
            },

            onFinish: (visit) => {
                processing.value = false;
            }
        }
    )
};

const setErrorIndex = () => {
    if (! props.errors) {
        return
    }

    errorIndex.value = [];

    fields.forEach((item, i) => {
        Object.keys(props.errors).forEach((err) => {
            if (item.includes(err)) {
                //console.log(i);
                if (!unref(errorIndex).includes(i)) {
                    errorIndex.value.push(i);
                }
            }
        });
    });
};

const validateEmail = async () => {
    v$.value.form.email.$touch()

    if (v$.value.$errors.length) {
        return
    }

    isValidating.value = true

    const { data } = await axios({
        method: "get",
        url: "/ajax/email-data",
        params: {
            email: form.email
        }
    })

    if (data.existing) {
        router.visit(`/customers/${data.data.uuid}/anamnesis`, { method: 'get' })

        return
    }

    isValidating.value = false
    isValidated.value = true

    v$.value.form.email.$touch()
}

const getHasTabErrors = (tab) => {
    if (! tabFields[tab]) {
        return false
    }

    tabFields[tab].forEach(v => {
        if (v$.value.form[v]) {
            v$.value.form[v].$touch()
        }
    })

    return Boolean(v$.value.$errors.length)
}
const goToTab = (to, from) => {
    if (getHasTabErrors(from)) {
        scrollToFirstError()
        return
    }

    active.value = to
}

const goToTabFromActive = (to) => {
    if (active.value == 'profile' && ! isValidated.value) {
        v$.value.form['email'].$touch()
        return
    }

    goToTab(to, active.value)
}

const changeEmail = () => {
    form.email = ''
    isValidated.value = false
}

const download = async () => {
    if (! processing.value) {
        await save(null, true)
    }
}

onMounted(() => {
    if (props.customer?.is_measurement_stale) {
        toast.add({
            severity: 'error',
            summary: 'Messung veraltet',
            detail: 'Die Messungen dieses Kunden wurden seit über 3 Wochen nicht aktualisiert.',
            life: 0,
            closable: true
        });
    }
});

watch(
    () => form.symptoms,
    (newVal, oldVal) => {
        if (newVal.length && form.is_no_findings) {
            form.is_no_findings = null
        }
    }
)

</script>
<template>
    <Layout>
        <div class="max-w-7xl mx-auto p-4 my-10">
            <div class="flex gap-4">
                <nav class="max-w-[250px]">
                    <li
                        :class="`${active == item.value ? ' text-black font-bold' : errorIndex.includes(i) ? ' text-red-500 font-bold ' : 'text-gray-500'} lato list-none my-4  cursor-pointer`"
                        v-for="(item, i) in tabs"
                        :key="i"
                        @click="goToTabFromActive(item.value)"
                    >
                        {{ item.name }}
                    </li>
                </nav>

                <div class="flex-1">
                    <div>
                        <div class="grid grid-cols-7 gap-4">
                            <div class="" v-for="(item, i) in tabs" :key="i">
                                <div
                                    @click="goToTabFromActive(item.value)"
                                    :class="`${active == item.value ? 'bg-[#D7D4CE]' : errorIndex.includes(i) ? ' bg-red-500/50' : 'bg-white '}  cursor-pointer h-3 rounded p-1 `"
                                ></div>
                            </div>
                        </div>

                        <div class="bg-white mt-4 mb-10 rounded">
                            <FormProfile
                                v-if="active == 'profile'"
                                @next="goToTab"
                                @validate="validateEmail"
                                @change-email="changeEmail"
                                @download="download"
                                :form="form"
                                :errors="errors"
                                :gender-options="genderOptions"
                                :is-validated="isValidated"
                                :is-validating="isValidating"
                                :validation-error="validationError"
                                :is-edit="isEdit"
                                :v$="v$"
                            />

                            <FormProgram
                                v-if="active == 'program'"
                                @next="goToTab"
                                :form="form"
                                :errors="errors"
                                :max-2-hours-options="max2HoursOptions"
                                :v$="v$"
                            />

                            <FormGoal
                                v-if="active == 'goals'"
                                @next="goToTab"
                                :errors="errors"
                                :form="form"
                                :toast="toast"
                                :v$="v$"
                            />

                            <FormAnamnese
                                v-if="active == 'anamnese'"
                                @next="goToTab"
                                :errors="errors"
                                :form="form"
                                :rate-fitness-options="rateFitnessOptions"
                                :physical-options="physicalOptions"
                                :discipline-options="disciplineOptions"
                                :drink-softdrink-options="drinkSoftdrinkOptions"
                                :drink-coffee-options="drinkCoffeeOptions"
                                :drink-tea-options="drinkTeaOptions"
                                :drink-alcohol-options="drinkAlcoholOptions"
                                :v$="v$"
                            />

                            <FormSymptoms
                                v-if="active == 'symptoms-and-diseases'"
                                @next="goToTab"
                                :errors="errors"
                                :form="form"
                                :stress-level-options="stressLevelOptions"
                                :v$="v$"
                            />

                            <FormMedicines
                                v-if="active == 'medicines'"
                                @next="goToTab"
                                :form="form"
                                :errors="errors"
                                :thyroid-gland-options="thyroidGlandOptions"
                                :v$="v$"
                            />

                            <FormSave
                                v-if="active == 'save'"
                                :form="form"
                                @save="save"
                                :processing="processing"
                                :errors="errors"
                                :is-edit="isEdit"
                                :v$="v$"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>
